import React, { useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import styled, { ThemeProvider } from "styled-components";
import { AdminThemeMode, adminThemes } from "../styles/adminTheme";
import { clearAuthSession, getStoredAccount } from "../utils/auth";

type AdminNavItem = {
  label: string;
  shortLabel: string;
  path: string;
  icon: React.ReactNode;
};

const DashboardIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 13h7V4H4v9Zm0 7h7v-5H4v5Zm9 0h7v-9h-7v9Zm0-16v5h7V4h-7Z" />
  </svg>
);

const IssuesIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 4h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm3 4v2h8V8H8Zm0 4v2h5v-2H8Zm0 4v2h8v-2H8Z" />
  </svg>
);

const BillingIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 7a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V7Zm3-1a1 1 0 0 0-1 1v1h12V7a1 1 0 0 0-1-1H7Zm11 6H6v5a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-5Zm-4 2h2v2h-2v-2Z" />
  </svg>
);

const DocsIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 3h9l5 5v13H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm8 1.5V9h4.5L14 4.5ZM8 13v2h8v-2H8Zm0 4v2h6v-2H8Z" />
  </svg>
);

const BellIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-6-1.6-2.1V10a5.42 5.42 0 0 0-4.4-5.32V3a1 1 0 1 0-2 0v1.68A5.42 5.42 0 0 0 6.6 10v3.9L5 16v2h14v-2Z" />
  </svg>
);

const MenuIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 6h16v2H4V6Zm0 5h16v2H4v-2Zm0 5h16v2H4v-2Z" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="m6.4 5 12.6 12.6-1.4 1.4L5 6.4 6.4 5Zm12.6 1.4L6.4 19 5 17.6 17.6 5 19 6.4Z" />
  </svg>
);

const navItems: AdminNavItem[] = [
  { label: "Dashboard", shortLabel: "Home", path: "/admin/dashboard", icon: <DashboardIcon /> },
  { label: "Issues", shortLabel: "Issues", path: "/admin/issues", icon: <IssuesIcon /> },
  { label: "Billing", shortLabel: "Billing", path: "/admin/billing", icon: <BillingIcon /> },
  { label: "Docs", shortLabel: "Docs", path: "/admin/docs", icon: <DocsIcon /> },
];

const ShellRoot = styled.div`
  min-height: 100vh;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.background};
`;

const Layout = styled.div`
  min-height: 100vh;
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);

  @media (max-width: 1180px) {
    grid-template-columns: 88px minmax(0, 1fr);
  }

  @media (max-width: 767px) {
    display: block;
    padding-bottom: 76px;
  }
`;

const Sidebar = styled.aside<{ $open: boolean }>`
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 24px 18px;
  background: ${({ theme }) => theme.colors.sidebar};
  color: ${({ theme }) => theme.colors.sidebarText};
  z-index: 60;

  @media (max-width: 1180px) {
    align-items: center;
    padding: 22px 14px;
  }

  @media (max-width: 767px) {
    position: fixed;
    inset: 0 auto 0 0;
    width: min(86vw, 320px);
    align-items: stretch;
    transform: translateX(${({ $open }) => ($open ? "0" : "-105%")});
    transition: transform 0.24s ease;
    box-shadow: ${({ theme }) => theme.colors.shadow};
  }
`;

const MobileBackdrop = styled.button<{ $open: boolean }>`
  display: none;

  @media (max-width: 767px) {
    position: fixed;
    inset: 0;
    display: ${({ $open }) => ($open ? "block" : "none")};
    background: rgba(15, 23, 42, 0.45);
    z-index: 55;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 34px;
`;

const BrandMark = styled.div`
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border-radius: 14px;
  color: #ffffff;
  font-weight: 900;
  background: linear-gradient(135deg, #2563eb, #38bdf8);
`;

const BrandCopy = styled.div`
  min-width: 0;

  strong {
    display: block;
    color: #ffffff;
    font-size: 16px;
    letter-spacing: -0.02em;
  }

  span {
    display: block;
    margin-top: 2px;
    color: #94a3b8;
    font-size: 12px;
    font-weight: 600;
  }

  @media (max-width: 1180px) and (min-width: 768px) {
    display: none;
  }
`;

const SidebarCloseButton = styled.button`
  display: none;

  @media (max-width: 767px) {
    position: absolute;
    top: 24px;
    right: 18px;
    display: grid;
    place-items: center;
    width: 38px;
    height: 38px;
    border-radius: 12px;
    color: #ffffff;
    background: rgba(255, 255, 255, 0.1);

    svg {
      width: 20px;
      height: 20px;
      fill: currentColor;
    }
  }
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NavItem = styled(NavLink)`
  min-height: 48px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 14px;
  border-radius: 16px;
  color: ${({ theme }) => theme.colors.sidebarText};
  font-size: 14px;
  font-weight: 800;
  transition: background 0.2s ease, color 0.2s ease;

  svg {
    width: 21px;
    height: 21px;
    flex: 0 0 auto;
    fill: currentColor;
  }

  &.active {
    color: ${({ theme }) => theme.colors.sidebarActive};
    background: ${({ theme }) => theme.colors.sidebarActiveBg};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.sidebarActive};
    background: ${({ theme }) => theme.colors.sidebarActiveBg};
  }

  @media (max-width: 1180px) and (min-width: 768px) {
    width: 52px;
    justify-content: center;
    padding: 0;

    span {
      display: none;
    }
  }
`;

const SidebarFooter = styled.div`
  margin-top: auto;
  padding: 16px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.06);

  strong {
    display: block;
    color: #ffffff;
    font-size: 13px;
  }

  p {
    margin-top: 6px;
    color: #94a3b8;
    font-size: 12px;
    line-height: 1.5;
  }

  @media (max-width: 1180px) and (min-width: 768px) {
    display: none;
  }
`;

const MainColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const TopHeader = styled.header`
  position: sticky;
  top: 0;
  z-index: 45;
  height: 76px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 0 clamp(18px, 3vw, 34px);
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: color-mix(in srgb, ${({ theme }) => theme.colors.background} 88%, transparent);
  backdrop-filter: blur(18px);

  @media (max-width: 767px) {
    height: 66px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
`;

const MenuButton = styled.button`
  display: none;

  @media (max-width: 767px) {
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    border-radius: 14px;
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.border};

    svg {
      width: 20px;
      height: 20px;
      fill: currentColor;
    }
  }
`;

const HeaderTitle = styled.div`
  min-width: 0;

  strong {
    display: block;
    color: ${({ theme }) => theme.colors.text};
    font-size: clamp(17px, 2vw, 20px);
    letter-spacing: -0.025em;
  }

  span {
    display: block;
    margin-top: 3px;
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 12px;
    font-weight: 700;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const IconButton = styled.button`
  position: relative;
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 14px;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};

  svg {
    width: 19px;
    height: 19px;
    fill: currentColor;
  }
`;

const NotificationDot = styled.span`
  position: absolute;
  top: 9px;
  right: 10px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.danger};
`;

const ThemeToggle = styled.button`
  height: 42px;
  padding: 0 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 14px;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 13px;
  font-weight: 800;

  @media (max-width: 767px) {
    display: none;
  }
`;

const ProfileButton = styled.button`
  height: 42px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px 0 6px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
`;

const Avatar = styled.span`
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: #ffffff;
  font-size: 12px;
  font-weight: 900;
  background: linear-gradient(135deg, #2563eb, #0f172a);
`;

const ProfileName = styled.span`
  font-size: 13px;
  font-weight: 800;

  @media (max-width: 767px) {
    display: none;
  }
`;

const Content = styled.main`
  flex: 1;
  padding: clamp(20px, 3vw, 34px);
`;

const BottomTabBar = styled.nav`
  display: none;

  @media (max-width: 767px) {
    position: fixed;
    left: 12px;
    right: 12px;
    bottom: 12px;
    z-index: 50;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
    padding: 8px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 24px;
    background: ${({ theme }) => theme.colors.surface};
    box-shadow: ${({ theme }) => theme.colors.shadow};
  }
`;

const BottomTab = styled(NavLink)`
  min-height: 54px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: 18px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 11px;
  font-weight: 800;

  svg {
    width: 19px;
    height: 19px;
    fill: currentColor;
  }

  &.active {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primarySoft};
  }
`;

const AppShell: React.FC = () => {
  const navigate = useNavigate();
  const [themeMode, setThemeMode] = useState<AdminThemeMode>("light");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const theme = useMemo(() => adminThemes[themeMode], [themeMode]);
  const account = getStoredAccount();
  const clientName = account?.client.name ?? "고객사";
  const avatarText = clientName.slice(0, 2).toUpperCase();

  const handleLogout = () => {
    clearAuthSession();
    navigate("/admin/login", { replace: true });
  };

  return (
    <ThemeProvider theme={theme}>
      <ShellRoot>
        <MobileBackdrop $open={mobileNavOpen} onClick={() => setMobileNavOpen(false)} aria-label="메뉴 닫기" />
        <Layout>
          <Sidebar $open={mobileNavOpen}>
            <SidebarCloseButton type="button" onClick={() => setMobileNavOpen(false)} aria-label="메뉴 닫기">
              <CloseIcon />
            </SidebarCloseButton>
            <Brand>
              <BrandMark>N</BrandMark>
              <BrandCopy>
                <strong>Neural OS</strong>
                <span>Client Admin</span>
              </BrandCopy>
            </Brand>

            <Nav aria-label="관리자 주요 메뉴">
              {navItems.map((item) => (
                <NavItem key={item.path} to={item.path} onClick={() => setMobileNavOpen(false)}>
                  {item.icon}
                  <span>{item.label}</span>
                </NavItem>
              ))}
            </Nav>

            <SidebarFooter>
              <strong>Enterprise Workspace</strong>
              <p>실시간 이슈, 비용, 문서, AI 에이전트를 통합 관리합니다.</p>
            </SidebarFooter>
          </Sidebar>

          <MainColumn>
            <TopHeader>
              <HeaderLeft>
                <MenuButton type="button" onClick={() => setMobileNavOpen(true)} aria-label="메뉴 열기">
                  <MenuIcon />
                </MenuButton>
                <HeaderTitle>
                  <strong>Neural OS Console</strong>
                  <span>{clientName} Production Workspace</span>
                </HeaderTitle>
              </HeaderLeft>

              <HeaderActions>
                <IconButton type="button" aria-label="알림">
                  <BellIcon />
                  <NotificationDot />
                </IconButton>
                <ThemeToggle
                  type="button"
                  onClick={() => setThemeMode((current) => (current === "light" ? "dark" : "light"))}
                >
                  {themeMode === "light" ? "Dark" : "Light"}
                </ThemeToggle>
                <ProfileButton type="button" onClick={handleLogout} aria-label="프로필 메뉴">
                  <Avatar>{avatarText}</Avatar>
                  <ProfileName>{account?.name ?? "Admin"}</ProfileName>
                </ProfileButton>
              </HeaderActions>
            </TopHeader>

            <Content>
              <Outlet />
            </Content>
          </MainColumn>
        </Layout>

        <BottomTabBar aria-label="모바일 관리자 메뉴">
          {navItems.map((item) => (
            <BottomTab key={item.path} to={item.path}>
              {item.icon}
              <span>{item.shortLabel}</span>
            </BottomTab>
          ))}
        </BottomTabBar>
      </ShellRoot>
    </ThemeProvider>
  );
};

export default AppShell;
